import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import TakeOffTooltip from '../TakeOff/TakeOffTooltip'
import {
  InfoIcon,
  LeftArrowIcon,
  RightArrowIcon,
  SavedIcon,
  SearchIcon,
  UploadFileIcon,
} from '../Svgs'
import { useEffect, useRef, useState } from 'react'
import { BimLibraryCategoryListComponent } from './BimLibraryCategoryListComponent'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { useDispatch, useSelector } from 'react-redux'
import { BimCluster } from '../../models/Bim'
import { groupBimObjects } from '../../utils/BimUtil'
import { setSelectedBimObjectId } from '../../actions/bim'

const bimObjectsSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.objects,
)

interface BimLibraryProps {}

const GroupButton = ({
  groupName,
  isSelected,
  handleClick,
}: {
  groupName: string
  isSelected: boolean
  handleClick: () => void
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isSelected) {
      buttonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [isSelected])

  return (
    <Button
      ref={buttonRef}
      variant='contained'
      disableElevation
      onClick={handleClick}
      sx={{
        fontSize: '0.92rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        textTransform: 'capitalize',
        borderRadius: '4px',
        color: isSelected ? 'white' : '#A3A3A3',
        textWrap: 'nowrap',
        minWidth: 'fit-content',
        backgroundColor: isSelected ? '#1705E5' : 'transparent',
      }}
    >
      {groupName.toLowerCase()}
    </Button>
  )
}

const BimLibraryComponent: React.FC<BimLibraryProps> = ({}) => {
  const dispatch = useDispatch<any>()
  const bimObjects = useSelector(bimObjectsSelector)
  const boxRef = useRef<HTMLDivElement>(null)
  const [bimClusters, setBimClusters] = useState<BimCluster[]>([])
  const [selectedGroup, setSelectedGroup] = useState<string>(
    groupBimObjects(bimObjects)[0].category,
  )
  const [boxHeight, setBoxHeight] = useState(0)

  useEffect(() => {
    setBimClusters(groupBimObjects(bimObjects))
  }, [bimObjects])

  useEffect(() => {
    dispatch(setSelectedBimObjectId(null))
  }, [selectedGroup])

  useEffect(() => {
    const calculateHeight = () => {
      if (boxRef.current) {
        setBoxHeight(boxRef.current.clientHeight)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  const handleLeftArrowClick = () => {
    setSelectedGroup((prevSelectedCategory) => {
      // If there is no selected group, set the first one available
      if (!prevSelectedCategory) {
        return bimClusters[0].category
      }

      const index = bimClusters.findIndex(
        (data) => data.category === prevSelectedCategory,
      )
      // If there is no key that equals to the selected group, set the first one available
      if (index < 0) {
        return bimClusters[0].category
      }

      // If selected group is the first element, select the last one
      if (index === 0) {
        return bimClusters[bimClusters.length - 1].category
      }

      // Otherwise, select the previous group
      return bimClusters[index - 1].category
    })
  }

  const handleRightArrowClick = () => {
    setSelectedGroup((prevSelectedCategory) => {
      // If there is no selected group, set the first one available
      if (!prevSelectedCategory) {
        return bimClusters[0].category
      }

      const index = bimClusters.findIndex(
        (data) => data.category === prevSelectedCategory,
      )
      // If there is no key that equals to the selected group, set the first one available
      if (index < 0) {
        return bimClusters[0].category
      }

      // If selected group is the last element, select the first one
      if (index === bimClusters.length - 1) {
        return bimClusters[0].category
      }

      // Otherwise, select the next group
      return bimClusters[index + 1].category
    })
  }

  return (
    <Stack
      gap='0.7rem'
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        padding: '0.5rem',
        background: '#fff',
        border: '1px solid #E9E9E9',
        borderRadius: '0.3rem',
        boxShadow: '0px 2.64px 4.4px 0px #2F2F2F40',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '4px',
            alignItems: 'baseline',
          }}
        >
          <Typography
            sx={{
              color: '#04002E',
              fontSize: '0.82rem',
              fontWeight: 500,
            }}
          >
            BIM Library Categories
          </Typography>
          <TakeOffTooltip title={''}>
            <InfoIcon sx={{ color: '#04002E', fontSize: 12 }} />
          </TakeOffTooltip>
        </Box>

        <Box
          gap='1rem'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton disableRipple sx={{ p: 0, height: '1rem' }}>
            <SearchIcon
              sx={{
                color: '#97999B',
                fontSize: '1rem',
                '&:hover': { color: '#1705E5' },
              }}
            />
          </IconButton>
          <Divider
            flexItem
            orientation='vertical'
            sx={{
              backgroundColor: '#EEEEEE',
            }}
          />
          <IconButton disableRipple sx={{ p: 0, height: '1rem' }}>
            <UploadFileIcon
              sx={{
                color: '#97999B',
                fontSize: '1rem',
                '&:hover': { color: '#1705E5' },
              }}
            />
          </IconButton>
          <Divider
            flexItem
            orientation='vertical'
            sx={{
              backgroundColor: '#EEEEEE',
            }}
          />
          <Button
            disableRipple
            size='small'
            startIcon={
              <Box
                component={SavedIcon}
                sx={{
                  color: '#97999B',
                  fontSize: '1rem',
                  transition: 'color 0.2s',
                }}
              />
            }
            sx={{
              color: '#97999B',
              textTransform: 'capitalize',
              fontSize: '0.72rem',
              p: 0,
              height: '1.5rem',
              transition: 'color 0.2s',
              backgroundColor: 'transparent',
              '&:hover': {
                color: '#1705E5',
                backgroundColor: 'transparent',
                '& .MuiButton-startIcon > *': {
                  color: '#1705E5',
                },
              },
            }}
          >
            Saved
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          gap: '10px',
          display: 'flex',
          padding: '0.25rem',
          borderRadius: '4px',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <>
          <Box
            ref={boxRef}
            sx={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <IconButton onClick={handleLeftArrowClick}>
              <LeftArrowIcon
                sx={{
                  color: '#1705E5',
                  fontSize: 14,
                }}
              />
            </IconButton>

            <Divider
              flexItem
              orientation='vertical'
              sx={{
                backgroundColor: '#BBBCBC',
              }}
            />

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                '&-ms-overflow-style:': {
                  display: 'none',
                },
              }}
            >
              {bimClusters.map((data) => (
                <GroupButton
                  key={data.category}
                  groupName={data.category}
                  isSelected={selectedGroup === data.category}
                  handleClick={() => setSelectedGroup(data.category)}
                />
              ))}
            </Box>

            <Divider
              flexItem
              orientation='vertical'
              sx={{
                backgroundColor: '#BBBCBC',
              }}
            />

            <IconButton onClick={handleRightArrowClick}>
              <RightArrowIcon
                sx={{
                  color: '#1705E5',
                  fontSize: 14,
                }}
              />
            </IconButton>
          </Box>

          <Divider
            flexItem
            sx={{
              backgroundColor: '#BBBCBC',
            }}
          />

          <Box sx={{ height: `calc(100% - ${boxHeight}px - 10px - 0.5rem)` }}>
            <BimLibraryCategoryListComponent
              subCategories={
                bimClusters.find((item) => item.category === selectedGroup)
                  ?.subcategories
              }
            />
          </Box>
        </>
      </Box>
    </Stack>
  )
}

export default BimLibraryComponent
